import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import exceedanceReportApiService from 'src/api/services/exceedanceReportApiService';
import { ExceedanceReportPageState } from './types';

const initialState: ExceedanceReportPageState = {
    requestId: undefined,
    status: 'initial',
    year: undefined,
    currentState: null
};

export const runReport = createAsyncThunk<void, void, { state: AppState }>(
    'exceedance-report/run',
    async (_, { getState }) => {
        const year = selectExceedanceReportYear(getState());
        if (!year) {
            throw new Error('Не задан год');
        }
        return await exceedanceReportApiService.runReport(year);
    }
);

export const loadExceedanceReportCurrentState = createAsyncThunk('exceedance-report/load-current-state', () => {
    return exceedanceReportApiService.loadCurrentState();
});

const slice = createSlice({
    name: 'statistics',
    initialState,
    reducers: {
        reset: () => initialState,
        yearChanged: (state, action: PayloadAction<number>) => {
            state.year = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(runReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'submit';
            })
            .addCase(runReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(runReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(loadExceedanceReportCurrentState.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.currentState = null;
            })
            .addCase(loadExceedanceReportCurrentState.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.currentState = action.payload;
                }
            })
            .addCase(loadExceedanceReportCurrentState.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            });
    }
});

export const { reset, yearChanged } = slice.actions;

const selectExceedanceReportPageState = (state: AppState) => state.exceedanceReport;

export const selectExceedanceReportStatus = (state: AppState) => selectExceedanceReportPageState(state).status;

export const selectExceedanceReportYear = (state: AppState) => selectExceedanceReportPageState(state).year;

export const selectExceedanceReportCurrentState = (state: AppState) =>
    selectExceedanceReportPageState(state).currentState;

export default slice.reducer;
